/*
 * Comparison bars — one metric compared across entities (players or teams) as a
 * color-coded bar each, with an avatar + name. Shared by the In Depth tabs
 * (multi-entity compare) and Visão Geral (a player vs their partner).
 */

import type React from 'react';
import { EntityAvatar } from './EntityAvatar';

export interface CmpDatum {
  id: string;
  name: string;
  crop?: string;
  /** Team rows: the members' crops, shown as a small avatar cluster. */
  crops?: string[];
  color: string;
  value: number;
  /** The number, without its unit. */
  display: string;
  /** Small dimmed unit shown inline after the number (e.g. "m", "m/s", "%"). */
  unit?: string;
}

const ValueCell: React.FC<{ d: CmpDatum }> = ({ d }) => (
  <div className="cmp-val">
    <span className="cmp-val-num">{d.display}</span>
    {d.unit ? <span className="cmp-val-unit">{d.unit}</span> : null}
  </div>
);

// One metric compared across entities: a bar each, color-coded, with avatar +
// name. The longest bar pins to 100% of the track.
export const CompareMetric: React.FC<{ label: string; data: CmpDatum[] }> = ({
  label,
  data,
}) => {
  const max = Math.max(...data.map((d) => d.value), 0) || 1;
  return (
    <div className="cmp-metric">
      {label ? <div className="cmp-metric-label">{label}</div> : null}
      <div className="cmp-rows">
        {data.map((d) => (
          <div key={d.id} className="cmp-row">
            <div className="cmp-id">
              <EntityAvatar
                name={d.name}
                color={d.color}
                crop={d.crop}
                crops={d.crops}
              />
              <span className="cmp-name">{d.name}</span>
            </div>
            <div className="cmp-track">
              {/* Neutral fill: the row's avatar (colored ring) + name already
                  identify the entity, so the bar carries only magnitude — one
                  tone keeps lengths honestly comparable across rows. */}
              <div
                className="cmp-fill"
                style={{ width: `${(d.value / max) * 100}%` }}
              />
            </div>
            <ValueCell d={d} />
          </div>
        ))}
      </div>
    </div>
  );
};

export interface MetricSpec {
  label: string;
  data: CmpDatum[];
}

// Multi-entity card body: a comparison bar per metric.
export const CompareGroup: React.FC<{ metrics: MetricSpec[] }> = ({
  metrics,
}) => (
  <div className="cmp-grid">
    {metrics.map((m) => (
      <CompareMetric key={m.label} label={m.label} data={m.data} />
    ))}
  </div>
);
